import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppSwitch from '../../../../app/components/CAppSwitch';
import { useAppNavigationContext } from '../../../../app/features/navigation/providers/AppNavigationProvider';
import { useAppSettings } from '../../../../app/features/settings/providers/AppSettingsProvider';
import IFund from '../../interfaces/fund/IFund';
import investmentActionLang from '../../langs/investmentActionLang';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';

type FInvestSelectCurrencySwitchProps = {
	funds: IFund[];
};

const FInvestSelectCurrencySwitch = (props: FInvestSelectCurrencySwitchProps) => {
	const { funds } = props;

	const { t } = useTranslation();
	const { realizationStatus: defaultRealizationStatus } = useAppSettings();

	const [fundsFiltered, setFundsFiltered] = useState<IFund[] | null>(null);
	useEffect(() => {
		if (funds) {
			if (['unrealized', 'realized'].includes(defaultRealizationStatus)) {
				setFundsFiltered(
					funds.filter(
						(x) =>
							(x.isRealized === false && defaultRealizationStatus === 'unrealized') ||
							(x.isRealized === true && defaultRealizationStatus === 'realized'),
					),
				);
			} else {
				setFundsFiltered(funds);
			}
		}
	}, [funds, defaultRealizationStatus]);

	const navigationContext = useAppNavigationContext();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const handleToggleCurrency = () => {
		navigationContext.setCurrentCurrency(navigationContext.currentCurrency === 'EUR' ? 'USD' : 'EUR');
	};

	const [currencies, setCurrencies] = useState<{ currency: TInvestFundCurrency; nbFunds: number }[] | null>(null);
	useEffect(() => {
		if (fundsFiltered && navigationContext) {
			const currencies = _.uniq(fundsFiltered.map((x) => x.currency));
			setCurrencies(
				_.orderBy(
					currencies.map((x) => ({
						currency: x,
						nbFunds: fundsFiltered.filter((f) => f.currency === x).length,
					})),
					['nbFunds'],
					['desc'],
				),
			);

			if (navigationContext.currentCurrency === null) {
				if (currencies && currencies.length !== 0) {
					navigationContext.setCurrentCurrency(currencies[0]);
				} else {
					navigationContext.setCurrentCurrency('EUR');
				}
			}
		}
	}, [fundsFiltered, navigationContext]);

	return (
		<>
			{navigationContext.displayCurrencySelector && fundsFiltered && currencies && currencies.length > 1 ? (
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography
						color="primary"
						sx={{ fontWeight: navigationContext.currentCurrency === 'EUR' ? 500 : 'normal' }}
					>
						{!isMobile ? (
							<>
								{t(investmentActionLang.switch_currency_x_fund.tag, {
									currency: 'EUR',
									count: currencies.find((x) => x.currency === 'EUR')?.nbFunds,
								})}
							</>
						) : (
							<>
								{t(investmentActionLang.switch_currency_x_fund_mobile.tag, {
									currency: 'EUR',
									count: currencies.find((x) => x.currency === 'EUR')?.nbFunds,
								})}
							</>
						)}
					</Typography>
					<CAppSwitch
						checked={navigationContext.currentCurrency === 'USD'}
						onChange={() => handleToggleCurrency()}
						id="switch-currency"
					/>
					<Typography
						color="primary"
						sx={{ fontWeight: navigationContext.currentCurrency === 'USD' ? 500 : 'normal' }}
					>
						{!isMobile ? (
							<>
								{t(investmentActionLang.switch_currency_x_fund.tag, {
									currency: 'USD',
									count: currencies.find((x) => x.currency === 'USD')?.nbFunds,
								})}
							</>
						) : (
							<>
								{t(investmentActionLang.switch_currency_x_fund_mobile.tag, {
									currency: 'USD',
									count: currencies.find((x) => x.currency === 'USD')?.nbFunds,
								})}
							</>
						)}
					</Typography>
				</Stack>
			) : (
				<></>
			)}
		</>
	);
};

export default FInvestSelectCurrencySwitch;
